import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";

export default function ForgotPasswordPage() {
    useSEO({
        title: "Forgot Password — Carbon Labs Australia",
        description: "Request a password reset link for your Carbon Labs Australia account.",
        canonical: "https://www.carbonlabsau.com/forgot-password",
    });

    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't submit request. Please try again.");
        }
        setSubmitting(false);
    };

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="forgot-password-page">
            <MolecularBg intensity="low" />
            <div className="container-x max-w-md mx-auto relative z-10">
                <Link to="/login" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--tiffany)] mb-8" data-testid="back-to-login">
                    <ArrowLeft size={12} /> Back to sign in
                </Link>
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tighter mb-3">
                    Reset your <span className="text-gold-gradient">password</span>
                </h1>
                <p className="text-sm text-[var(--text-secondary)] mb-8 leading-relaxed">
                    Enter the email address you use for Carbon Labs and we'll send you a secure, time-limited link to reset your password.
                </p>

                {sent ? (
                    <div className="glass-elevated rounded-2xl p-6 flex items-start gap-3" data-testid="forgot-password-sent">
                        <CheckCircle2 size={20} className="text-[var(--tiffany)] shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-white mb-1">Check your inbox</p>
                            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                                If an account exists for <strong className="text-white">{email}</strong>, a reset link has been sent. The link expires in 60 minutes. Remember to check your spam folder.
                            </p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="glass-elevated rounded-2xl p-6 space-y-4" data-testid="forgot-password-form">
                        <div>
                            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">Email</label>
                            <div className="relative">
                                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="w-full min-w-0 pl-9 pr-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>
                        <button type="submit" disabled={submitting} className="btn-solid w-full inline-flex items-center justify-center gap-2" data-testid="forgot-submit-btn">
                            {submitting ? "Sending…" : "Send reset link"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
